/**
 * SessionManager - Auth message handler and case update manager
 * Acts as a bridge between parent window (Core Server) and Sol application components
 * Handles authentication, provider data, and case status updates
 */
import { useEffect, useRef } from 'react';
import { saveAuthData, getAuthData, clearAuthData, decodeJwt, isTokenExpired, 
         saveCaseStatus, getCaseStatus, updateCaseProgress, saveSessionActivity } from '../utils/sessionStorage';
import { updateProviderCaseStatus, updateVideoSessionProgress, completeProviderSession } from '../services/api';
import { fetchBackendApiWithBearer, getApiUrl } from '../utils/apiUtils';
import { executeBiometricAuth, hasBiometricAuthExecuted, resetBiometricAuthState, checkNativePlatform } from '../utils/nativeApiUtils';

const SessionManager = ({ onTokenValidated, onProviderDataLoaded, onError }) => {
  // Track if we've already sent the auto-load message
  const hasAutoLoaded = useRef(false);

  // Validate token with backend
  const validateToken = async (apiToken) => {
    try {
      const response = await fetchBackendApiWithBearer(
        '/admin/token/system/verify/',
        apiToken,
        {
          method: 'POST',
          body: JSON.stringify({
            api_token: apiToken
          })
        }
      );

      const data = await response.json();
      return { response, data };
    } catch (error) {
      throw new Error(`Network error: ${error.message}`);
    }
  };

  // Fetch provider details
  const fetchProviderDetails = async (providerId, apiToken) => {
    try {
      const response = await fetchBackendApiWithBearer(
        `/admin/providers/${providerId}/`,
        apiToken,
        {
          method: 'GET'
        }
      );

      const data = await response.json();
      
      if (response.ok && data.success) {
        return data.provider;
      } else {
        throw new Error(data.error || 'Failed to fetch provider details');
      }
    } catch (error) {
      throw new Error(`Provider fetch error: ${error.message}`);
    }
  };


  // Handle token validation request from any component (Core Server, IframeTester, etc.)
  const handleTokenValidationRequest = async (payload) => {
    const { api_token, request_id } = payload;
    
    try {
      // Validate token with backend
      const { response, data } = await validateToken(api_token);
      
      if (response.ok && data.success) {
        // Decode JWT for additional info
        const decoded = decodeJwt(api_token);
        const expired = decoded ? isTokenExpired(decoded) : true;
        
        let providerData = null;
        
        // If it's a healthcare provider token and not expired, fetch provider details
        if (data.api_token_type === 'core_server_healthcare_provider' && decoded && !expired && decoded.provider_id) {
          try {
            providerData = await fetchProviderDetails(decoded.provider_id, api_token);
          } catch (error) {
            // console.warn('Failed to fetch provider details:', error.message);
          }
        }
        
        // Save successful validation to localStorage
        const authData = {
          api_token,
          token_info: data,
          decoded_jwt: decoded,
          expired,
          provider_data: providerData,
          validated_at: new Date().toISOString()
        };
        saveAuthData(authData, data);

        // Send success response back to requesting component
        window.postMessage({
          type: 'TOKEN_VALIDATION_RESPONSE',
          payload: {
            request_id,
            success: true,
            token_info: data,
            decoded_jwt: decoded,
            expired,
            provider_data: providerData
          }
        }, window.location.origin);
        
        // Notify parent component
        if (onTokenValidated) {
          onTokenValidated({
            token_info: data,
            decoded_jwt: decoded,
            expired,
            provider_data: providerData
          });
        }
        
      } else {
        // Send error response back to requesting component
        window.postMessage({
          type: 'TOKEN_VALIDATION_RESPONSE',
          payload: {
            request_id,
            success: false,
            error: data.error || 'Token validation failed'
          }
        }, window.location.origin);
      }
      
    } catch (error) {      
      // Send error response back to requesting component
      window.postMessage({
        type: 'TOKEN_VALIDATION_RESPONSE',
        payload: {
          request_id,
          success: false,
          error: error.message
        }
      }, window.location.origin);
      
      if (onError) {
        onError(error);
      }
    }
  };

  // ========================================
  // CASE UPDATE FUNCTIONALITY
  // ========================================

  // Update case status (similar to Mira's updatePatientStatus)
  const updateCaseStatus = async (caseId, status, additionalData = {}) => {
    const authData = getAuthData();
    if (!authData?.api_token || !caseId) {
      console.warn('[SessionManager] Missing API token or case ID for case update');
      return;
    }
    try {
      console.log(`[SessionManager] Updating case ${caseId} status to: ${status}`);
      // Call backend API to update case status
      const response = await updateProviderCaseStatus(authData.api_token, caseId, status, additionalData);      
      // Update local storage for caching
      const statusData = {
        status,
        ...additionalData,
        updated_at: new Date().toISOString()
      };
      updateCaseProgress(caseId, statusData);
      console.log('[SessionManager] Case status updated successfully:', response);      
      // Notify parent about successful case update
      window.postMessage({
        type: 'CASE_STATUS_UPDATED',
        payload: {
          caseId,
          status,
          additionalData,
          success: true
        }
      }, window.location.origin);
      
      return response;
    } catch (error) {
      console.error('[SessionManager] Error updating case status:', error);
      
      // Notify parent about failed case update
      window.postMessage({
        type: 'CASE_STATUS_UPDATE_FAILED',
        payload: {
          caseId,
          status,
          error: error.message
        }
      }, window.location.origin);
      
      throw error;
    }
  };

  // Update session progress
  const updateSessionProgress = async (caseId, progressData) => {
    const authData = getAuthData();
    if (!authData?.api_token || !caseId) {
      console.warn('[SessionManager] Missing API token or case ID for session progress update');
      return;
    }

    try {
      console.log(`[SessionManager] Updating session progress for case ${caseId}:`, progressData);
      
      // Call backend API
      const response = await updateVideoSessionProgress(authData.api_token, caseId, progressData);
      
      // Update local activity tracking
      saveSessionActivity(caseId, progressData);

      // Notify parent about session progress update
      window.postMessage({
        type: 'SESSION_PROGRESS_UPDATED',
        payload: {
          caseId,
          progressData,
          success: true
        }
      }, window.location.origin);

      return response;
    } catch (error) {
      console.error('[SessionManager] Error updating session progress:', error);
      
      window.postMessage({
        type: 'SESSION_PROGRESS_UPDATE_FAILED',
        payload: {
          caseId,
          progressData,
          error: error.message
        }
      }, window.location.origin);
      
      throw error;
    }
  };

  // Complete provider session (similar to Mira's completeCheckIn)
  const completeSession = async (caseId, sessionSummary = {}) => {
    const authData = getAuthData();
    if (!authData?.api_token || !caseId) {
      console.warn('[SessionManager] Missing API token or case ID for session completion');
      return;
    }

    try {
      const completionData = {
        completed_at: new Date().toISOString(),
        ...sessionSummary
      };

      console.log(`[SessionManager] Completing session for case ${caseId}:`, completionData);
      
      // Call backend API to complete session
      const response = await completeProviderSession(authData.api_token, caseId, completionData);
      
      // Update case status
      await updateCaseStatus(caseId, 'session_completed', completionData);

      // Clear session activity from storage
      const key = `sol_session_activity_${caseId}`;
      localStorage.removeItem(key);

      console.log('[SessionManager] Session completed successfully:', response);
      
      // Notify parent about session completion
      window.postMessage({
        type: 'SESSION_COMPLETED',
        payload: {
          caseId,
          sessionSummary: completionData,
          success: true
        }
      }, window.location.origin);
      
      return response;
    } catch (error) {
      console.error('[SessionManager] Error completing session:', error);
      
      window.postMessage({
        type: 'SESSION_COMPLETION_FAILED',
        payload: {
          caseId,
          sessionSummary,
          error: error.message
        }
      }, window.location.origin);
      
      throw error;
    }
  };

  // Handle case update requests from components
  const handleCaseUpdateRequest = async (payload) => {
    const { type, caseId, data } = payload;
    
    switch (type) {
      case 'UPDATE_STATUS':
        return await updateCaseStatus(caseId, data.status, data.additionalData);
      case 'UPDATE_PROGRESS':
        return await updateSessionProgress(caseId, data);
      case 'COMPLETE_SESSION':
        return await completeSession(caseId, data);
      case 'START_SESSION':
        return await updateCaseStatus(caseId, 'provider_joined', { 
          session_started: true, 
          start_time: new Date().toISOString(),
          ...data 
        });
      case 'START_AUDIO_SESSION':
        return await updateSessionProgress(caseId, {
          audioSessionStarted: true,
          audioSessionType: data.sessionType, // 'zoom', 'twilio', 'in-person'
          audioSessionId: data.sessionId,
          audioConnectionStatus: 'connected',
          audioSessionStartTime: new Date().toISOString(),
          realtimeTranscriptionEnabled: data.realtimeTranscription || false
        });
      case 'UPDATE_AUDIO_SESSION':
        return await updateSessionProgress(caseId, {
          audioConnectionStatus: data.connectionStatus,
          audioSessionDuration: data.duration,
          audioSegmentsCount: data.segmentsCount,
          lastAudioUpdate: new Date().toISOString(),
          realtimeTranscriptionActive: data.transcriptionActive || false
        });
      case 'STOP_AUDIO_SESSION':
        return await updateSessionProgress(caseId, {
          audioSessionEnded: true,
          audioSessionEndTime: new Date().toISOString(),
          audioConnectionStatus: 'disconnected',
          finalAudioDuration: data.duration,
          finalSegmentsCount: data.segmentsCount,
          audioProcessingStatus: data.processingStatus || 'pending'
        });
      case 'UPDATE_AUDIO_TRANSCRIPTION':
        return await updateSessionProgress(caseId, {
          transcriptionLength: data.length,
          transcriptionSegments: data.segments,
          lastTranscriptionUpdate: new Date().toISOString(),
          realtimeTranscriptionActive: data.active
        });
      case 'UPDATE_VISIT_NOTE':
        return await updateSessionProgress(caseId, {
          visitNoteGenerated: data.generated,
          visitNoteLength: data.length,
          visitNoteStatus: data.status, // 'generating', 'completed', 'error'
          lastVisitNoteUpdate: new Date().toISOString()
        });
      default:
        console.warn(`[SessionManager] Unknown case update type: ${type}`);
    }
  };

  // ========================================
  // END CASE UPDATE FUNCTIONALITY
  // ========================================

  // Initialize SessionManager - load saved auth data
  useEffect(() => {
    const autoUnlock = async () => {
      const savedAuthData = getAuthData();
      if (!savedAuthData) return;

      // Check if token is still valid
      const decoded = savedAuthData.decoded_jwt;
      const expired = decoded ? isTokenExpired(decoded) : true;

      if (expired) {
        // Saved token expired, clear it
        clearAuthData();
        return;
      }

      // Check platform to determine if biometric auth is needed
      let isNative = false;
      try {
        isNative = await checkNativePlatform();
      } catch (e) {
        console.warn('[SessionManager] Platform check failed, assuming web', e);
        isNative = false;
      }
      
      if (isNative) {
        // On native platforms, check if biometric already executed to prevent duplicates
        if (await hasBiometricAuthExecuted()) {
          console.log('[SessionManager] Biometric auth already executed on native – proceeding');
        } else {
          // Execute biometric authentication on native platforms
          // Will exit app if user cancels authentication
          let authSuccess = false;
          try {
            authSuccess = await executeBiometricAuth({
              delayMs: 1500,
              reason: 'Unlock Sol',
              exitOnCancel: true
            });
          } catch (e) {
            console.error('[SessionManager] Biometric execution error', e);
          }

          // If biometric auth failed and didn't exit, return early
          if (!authSuccess) {
            return;
          }
        }
      } else {
        // On web platform, skip biometric and proceed directly to token restoration
        console.debug('[SessionManager] Web platform detected, skipping biometric auth');
      }

      // Token is valid and (if native) biometric succeeded: notify parent and send message
      if (onTokenValidated) {
        onTokenValidated({
          token_info: savedAuthData.token_info,
          decoded_jwt: savedAuthData.decoded_jwt,
          expired: false,
          provider_data: savedAuthData.provider_data,
          from_storage: true
        });
      }

      // Also send stored token data back to IframeTester (acting as Core Server surrogate)
      // Only send once to prevent infinite loop
      if (!hasAutoLoaded.current) {
        hasAutoLoaded.current = true;
        window.postMessage({
          type: 'TOKEN_VALIDATION_RESPONSE',
          payload: {
            request_id: 'auto_load_' + Date.now(),
            success: true,
            token_info: savedAuthData.token_info,
            decoded_jwt: savedAuthData.decoded_jwt,
            expired: false,
            provider_data: savedAuthData.provider_data,
            api_token: savedAuthData.api_token, // Send back the actual JWT
            from_storage: true
          }
        }, window.location.origin);
      }
    };

    autoUnlock();
  }, []); // Empty dependency array - only run on mount

  // Message handler for postMessage communication
  useEffect(() => {
    const handleMessage = (event) => {
      // Only handle messages from same origin (for security)
      if (event.origin !== window.location.origin) {
        return;
      }
      const { type, payload } = event.data;
      switch (type) {
        case 'VALIDATE_TOKEN_REQUEST':
          handleTokenValidationRequest(payload);
          break;
        case 'CLEAR_STORED_TOKEN':
          clearAuthData();
          
          // Reset auto-load flag so it can load again after new auth
          hasAutoLoaded.current = false;
          
          // Clear the “biometric already executed” flag so the next login requires fresh auth
          resetBiometricAuthState();
          
          // Notify parent component (App.js) to clear its authentication state
          if (onTokenValidated) {
            onTokenValidated({
              cleared: true,
              token_info: null,
              decoded_jwt: null,
              provider_data: null
            });
          }
          
          window.postMessage({
            type: 'TOKEN_CLEARED',
            payload: { success: true }
          }, window.location.origin);
          break;
        
        // Handle case update requests
        case 'CASE_UPDATE_REQUEST':
          handleCaseUpdateRequest(payload).catch(error => {
            console.error('[SessionManager] Case update request failed:', error);
            if (onError) {
              onError(error);
            }
          });
          break;
          
        default:
          // Ignore other message types
          break;
      }
    };

    window.addEventListener('message', handleMessage);
    // console.log('SessionManager: Message listener registered');

    return () => {
      window.removeEventListener('message', handleMessage);
      // console.log('SessionManager: Message listener removed');
    };
  }, [onTokenValidated, onProviderDataLoaded, onError]);

  // SessionManager doesn't render anything
  return null;
};

export default SessionManager;